import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import MenuButton from "../MenuButton/MenuButton";
import { NoteState } from "../../context/notes/NoteState";
import UserServices from "../../UserServices/UserServices";
import axios from "axios";
import authHeader from "../../UserServices/auth-header";
import Pagination from "react-js-pagination";
import { toast } from "react-toastify";
import moment from "moment";
import jsPDF from "jspdf";
import "jspdf-autotable";
import "./ActiveClientsReport.css";
import {
  canUseAllBranches,
  filterBranchOptions,
  isCrossBranchAdmin,
} from "../../utils/branchAccess";

const VIEW_MODES = [
  { key: "list", label: "List", icon: "bi-list-ul" },
  { key: "detailed", label: "Detailed", icon: "bi-table" },
  { key: "management", label: "Management", icon: "bi-bar-chart-line" },
  { key: "by_category", label: "By Category", icon: "bi-grid" },
  { key: "expiring", label: "Expiring Soon", icon: "bi-exclamation-triangle" },
];

const EXPIRY_FILTERS = [
  { value: "all", label: "All expiry windows" },
  { value: "30", label: "Expiring within 1 month" },
  { value: "60", label: "Expiring within 2 months" },
  { value: "90", label: "Expiring within 3 months" },
  { value: "beyond_90", label: "More than 3 months left" },
  { value: "expired", label: "Past expiry (still active)" },
];

const QUICK_DATES = [
  { key: "today", label: "Expires today" },
  { key: "week", label: "Next 7 days" },
  { key: "month", label: "This month" },
  { key: "30", label: "Next 30 days" },
  { key: "60", label: "Next 60 days" },
  { key: "90", label: "Next 90 days" },
  { key: "clear", label: "Clear dates" },
];

const CATEGORY_OPTIONS = [
  { value: "", label: "All categories" },
  { value: "1", label: "Gym" },
  { value: "2", label: "Personal Trainer" },
  { value: "3", label: "Guest Pass" },
  { value: "4", label: "Small Group PT" },
  { value: "5", label: "Nutrition" },
  { value: "7", label: "Boot Camp" },
  { value: "9", label: "General" },
  { value: "11", label: "Vostro Fitness Academy" },
  { value: "14", label: "Physiotherapy" },
];

const DETAIL_TABS = [
  { key: "overview", label: "Overview", icon: "bi-person-badge" },
  { key: "packages", label: "Active packages", icon: "bi-box-seam" },
  { key: "history", label: "Package history", icon: "bi-clock-history" },
  { key: "gym", label: "Gym check-ins", icon: "bi-door-open" },
  { key: "sessions", label: "PT sessions", icon: "bi-calendar-check" },
  { key: "payments", label: "Payments", icon: "bi-credit-card" },
  { key: "freezing", label: "Freezing", icon: "bi-snow" },
  { key: "cards", label: "Access cards", icon: "bi-credit-card-2-front" },
];

const riskBadge = (risk) => {
  const map = {
    critical: { cls: "acr-risk acr-risk--critical", text: "≤ 1 month" },
    warning: { cls: "acr-risk acr-risk--warning", text: "≤ 2 months" },
    expired: { cls: "acr-risk acr-risk--expired", text: "Expired" },
    ok: { cls: "acr-risk acr-risk--ok", text: "OK" },
  };
  const r = map[risk] || map.ok;
  return <span className={r.cls}>{r.text}</span>;
};

const fmtDate = (d) => (d && d !== "0000-00-00" ? moment(d).format("DD MMM YYYY") : "—");

const DEFAULT_APPLIED_FILTERS = {
  clientId: "",
  search: "",
  category: "",
  packageId: "",
  expiryWindow: "all",
  expiryFrom: "",
  expiryTo: "",
  startFrom: "",
  startTo: "",
  gender: "",
  clientType: "",
  saleType: "",
  riskLevel: "",
  trainerName: "",
  freezeStatus: "",
  sortBy: "nearest_expiry",
  sortDir: "asc",
  limit: 25,
};

const filterClientsForPicker = (term, source) => {
  const list = source || [];
  const v = (term || "").trim().toLowerCase();
  if (!v) return list;
  return list.filter(
    (c) =>
      c.name.toLowerCase().includes(v) ||
      (c.phone && c.phone.includes(v)) ||
      (c.uid && String(c.uid).toLowerCase().includes(v))
  );
};

export default function ActiveClientsReport() {
  const { state } = useContext(NoteState);
  const { branch_id, role } = state;

  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [summary, setSummary] = useState(null);
  const [management, setManagement] = useState(null);
  const [pagination, setPagination] = useState({ total_record: 0, current_page: 1, per_page: 25 });
  const [viewMode, setViewMode] = useState("list");
  const [applied, setApplied] = useState(DEFAULT_APPLIED_FILTERS);

  const [clientSearchTerm, setClientSearchTerm] = useState("");
  const [draftClientId, setDraftClientId] = useState("");
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [allClients, setAllClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const clientDropdownRef = useRef(null);

  const [selectedBranchId, setSelectedBranchId] = useState(() => String(branch_id || ""));
  const [category, setCategory] = useState("");
  const [packageId, setPackageId] = useState("");
  const [expiryWindow, setExpiryWindow] = useState("all");
  const [expiryFrom, setExpiryFrom] = useState("");
  const [expiryTo, setExpiryTo] = useState("");
  const [startFrom, setStartFrom] = useState("");
  const [startTo, setStartTo] = useState("");
  const [gender, setGender] = useState("");
  const [clientType, setClientType] = useState("");
  const [saleType, setSaleType] = useState("");
  const [riskLevel, setRiskLevel] = useState("");
  const [trainerName, setTrainerName] = useState("");
  const [freezeStatus, setFreezeStatus] = useState("");
  const [sortBy, setSortBy] = useState("nearest_expiry");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [showMoreFilters, setShowMoreFilters] = useState(false);

  const [branches, setBranches] = useState([]);
  const [packages, setPackages] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [collapsedCategories, setCollapsedCategories] = useState(new Set());

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTab, setDetailTab] = useState("overview");
  const [detail, setDetail] = useState(null);
  const [exporting, setExporting] = useState(false);

  const resetDraftFilters = () => {
    setCategory("");
    setPackageId("");
    setExpiryWindow("all");
    setExpiryFrom("");
    setExpiryTo("");
    setStartFrom("");
    setStartTo("");
    setGender("");
    setClientType("");
    setSaleType("");
    setRiskLevel("");
    setTrainerName("");
    setFreezeStatus("");
    setSortBy("nearest_expiry");
    setSortDir("asc");
    setLimit(25);
  };

  const clearClientSearch = () => {
    setClientSearchTerm("");
    setDraftClientId("");
    setShowClientDropdown(false);
    setFilteredClients([]);
  };

  useEffect(() => {
    // Non-admin users are locked to their login branch (API also enforces this).
    if (!isCrossBranchAdmin(role)) {
      setSelectedBranchId(String(branch_id || ""));
    } else {
      setSelectedBranchId((prev) => (prev === "all" ? prev : String(branch_id || "")));
    }
    clearClientSearch();
    resetDraftFilters();
    setApplied({ ...DEFAULT_APPLIED_FILTERS });
  }, [branch_id, role]);

  useEffect(() => {
    const handleClick = (e) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(e.target)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const branchChoices = useMemo(
    () => filterBranchOptions(role, branch_id, branches),
    [role, branch_id, branches]
  );

  const effectiveBranchId = !isCrossBranchAdmin(role)
    ? String(branch_id || "")
    : selectedBranchId === "all"
      ? "all"
      : selectedBranchId || String(branch_id || "");

  const selectedBranchLabel = useMemo(() => {
    if (effectiveBranchId === "all") return "All branches";
    const match = branches.find((b) => String(b.id) === String(effectiveBranchId));
    return match?.name || summary?.branch_name || "Selected branch";
  }, [effectiveBranchId, branches, summary?.branch_name]);

  const loadClientsForPicker = useCallback(async () => {
    try {
      const params = effectiveBranchId !== "all" ? { branch_id: effectiveBranchId } : {};
      const res = await axios.get(
        process.env.REACT_APP_API_Link + "/clients/client-name",
        { headers: authHeader(), params }
      );
      if (res?.data?.status) {
        const mapped = (res.data.data || []).map((c) => ({
          id: c.id,
          uid: c.uid,
          name: `${c.first_name || ""} ${c.last_name || ""}`.trim(),
          phone: c.phone || "",
        }));
        setAllClients(mapped);
        setFilteredClients(mapped);
      } else {
        setAllClients([]);
        setFilteredClients([]);
      }
    } catch (e) {
      console.log(e);
      setAllClients([]);
      setFilteredClients([]);
    }
  }, [effectiveBranchId]);

  const loadBranches = useCallback(async () => {
    try {
      const res = await axios.get(
        process.env.REACT_APP_API_Link + "/branches/branches-name-list",
        { headers: authHeader(), params: { status: 1 } }
      );
      if (res?.data?.status) setBranches(res.data.data || []);
    } catch (e) {
      console.log(e);
    }
  }, []);

  const loadPackages = useCallback(async () => {
    try {
      const pkgBranch = effectiveBranchId === "all" ? branch_id : effectiveBranchId;
      const res = await axios.get(
        process.env.REACT_APP_API_Link + "/packages/all-with-categories",
        { headers: authHeader(), params: { branch_id: pkgBranch, status: "2" } }
      );
      if (res?.data?.status) {
        const mapped = (res.data.data || []).map((p) => ({
          id: p.id,
          category: p.category,
          name: (p.package_name || p.name || "").trim(),
        })).filter((p) => p.id && p.name);
        setPackages(mapped);
      } else {
        setPackages([]);
      }
    } catch (e) {
      console.log(e);
      setPackages([]);
    }
  }, [branch_id, effectiveBranchId]);

  const buildParams = useCallback(
    (pageNo = page, overrides = {}) => ({
      branch_id: effectiveBranchId || branch_id,
      client_id: applied.clientId || undefined,
      search: applied.search || undefined,
      category: applied.category || undefined,
      package_id: applied.packageId || undefined,
      expiry_window: applied.expiryWindow !== "all" ? applied.expiryWindow : undefined,
      expiry_from: applied.expiryFrom || undefined,
      expiry_to: applied.expiryTo || undefined,
      start_from: applied.startFrom || undefined,
      start_to: applied.startTo || undefined,
      gender: applied.gender || undefined,
      client_type: applied.clientType || undefined,
      sale_type: applied.saleType || undefined,
      risk_level: applied.riskLevel || undefined,
      trainer_name: applied.trainerName || undefined,
      freeze_status: applied.freezeStatus || undefined,
      sort_by: applied.sortBy,
      sort_dir: applied.sortDir,
      page: pageNo,
      limit: applied.limit,
      ...overrides,
    }),
    [effectiveBranchId, branch_id, applied, page]
  );

  const loadReport = useCallback(async (pageNo = 1) => {
    setLoading(true);
    try {
      const res = await UserServices.getActiveClientsReport(buildParams(pageNo));
      if (res?.data?.status) {
        setClients(res.data.data?.clients || []);
        setSummary(res.data.data?.summary || null);
        setManagement(res.data.data?.management || null);
        setPagination(res.data.pagination || { total_record: 0, current_page: pageNo, per_page: applied.limit });
        setPage(pageNo);
      } else {
        setClients([]);
        setSummary(null);
        setManagement(null);
      }
    } catch (e) {
      console.log(e);
      setClients([]);
      toast.error("Failed to load report");
    }
    setLoading(false);
  }, [buildParams, applied.limit]);

  useEffect(() => { loadBranches(); }, [loadBranches]);
  useEffect(() => { loadPackages(); }, [loadPackages]);
  useEffect(() => { loadClientsForPicker(); }, [loadClientsForPicker]);

  useEffect(() => {
    loadReport(1);
  }, [effectiveBranchId, applied]);

  useEffect(() => {
    if (viewMode === "expiring" && !riskLevel && expiryWindow === "all") {
      setExpiryWindow("30");
      setSortBy("nearest_expiry");
      setSortDir("asc");
    }
  }, [viewMode]);

  const handleClientSearch = (e) => {
    const v = e.target.value;
    setClientSearchTerm(v);
    setShowClientDropdown(true);
    setFilteredClients(filterClientsForPicker(v, allClients));
    if (!v.trim()) setDraftClientId("");
  };

  const handleClientSelect = (client) => {
    setDraftClientId(String(client.id));
    setClientSearchTerm(client.name);
    setShowClientDropdown(false);
  };

  const handleApplySearch = () => {
    setApplied({
      clientId: draftClientId,
      search: draftClientId ? "" : clientSearchTerm.trim(),
      category,
      packageId,
      expiryWindow,
      expiryFrom,
      expiryTo,
      startFrom,
      startTo,
      gender,
      clientType,
      saleType,
      riskLevel,
      trainerName: trainerName.trim(),
      freezeStatus,
      sortBy,
      sortDir,
      limit,
    });
  };

  const handlePerPageChange = (value) => {
    const nextLimit = Number(value);
    setLimit(nextLimit);
    setApplied((prev) => ({ ...prev, limit: nextLimit }));
  };

  const fetchAllFilteredClients = async () => {
    const res = await UserServices.getActiveClientsReport(
      buildParams(1, { export_all: true, page: 1 })
    );
    if (res?.data?.status) {
      return res.data.data?.clients || [];
    }
    throw new Error(res?.data?.message || "Export failed");
  };

  const applyQuickDate = (key) => {
    const today = moment();
    switch (key) {
      case "today":
        setExpiryFrom(today.format("YYYY-MM-DD"));
        setExpiryTo(today.format("YYYY-MM-DD"));
        setExpiryWindow("all");
        break;
      case "week":
        setExpiryFrom(today.format("YYYY-MM-DD"));
        setExpiryTo(today.clone().add(6, "days").format("YYYY-MM-DD"));
        setExpiryWindow("all");
        break;
      case "month":
        setExpiryFrom(today.format("YYYY-MM-DD"));
        setExpiryTo(today.clone().endOf("month").format("YYYY-MM-DD"));
        setExpiryWindow("all");
        break;
      case "30":
        setExpiryFrom("");
        setExpiryTo("");
        setExpiryWindow("30");
        break;
      case "60":
        setExpiryFrom("");
        setExpiryTo("");
        setExpiryWindow("60");
        break;
      case "90":
        setExpiryFrom("");
        setExpiryTo("");
        setExpiryWindow("90");
        break;
      case "clear":
        setExpiryFrom("");
        setExpiryTo("");
        setExpiryWindow("all");
        break;
      default:
        break;
    }
  };

  const resetFilters = () => {
    clearClientSearch();
    resetDraftFilters();
    setSelectedBranchId(String(branch_id || ""));
    setApplied({ ...DEFAULT_APPLIED_FILTERS });
  };

  const handleBranchChange = (value) => {
    if (!isCrossBranchAdmin(role)) {
      setSelectedBranchId(String(branch_id || ""));
      return;
    }
    setSelectedBranchId(value);
    setCategory("");
    setPackageId("");
    clearClientSearch();
    resetDraftFilters();
    setApplied({ ...DEFAULT_APPLIED_FILTERS });
  };

  const openDetail = async (clientId) => {
    setDetailOpen(true);
    setDetailTab("overview");
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await UserServices.getActiveClientDetail(clientId);
      if (res?.data?.status) setDetail(res.data.data);
      else toast.error(res?.data?.message || "Could not load client details");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not load client details");
    }
    setDetailLoading(false);
  };

  const packageOptions = useMemo(() => {
    const filtered = category
      ? packages.filter((p) => String(p.category) === String(category))
      : packages;
    return filtered.filter(
      (p) => p.name && !["6", "8", "10", "13"].includes(String(p.category))
    );
  }, [packages, category]);

  const clientsByCategory = useMemo(() => {
    const map = new Map();
    clients.forEach((c) => {
      (c.categories || ["Other"]).forEach((cat) => {
        if (!map.has(cat)) map.set(cat, []);
        map.get(cat).push(c);
      });
    });
    return [...map.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [clients]);

  const displayClients = useMemo(() => {
    if (viewMode === "expiring") {
      return [...clients].sort((a, b) => (a.days_until_expiry ?? 999) - (b.days_until_expiry ?? 999));
    }
    return clients;
  }, [clients, viewMode]);

  const exportCSV = async () => {
    if (!pagination.total_record) {
      toast.info("No clients to export");
      return;
    }
    setExporting(true);
    try {
      const rows = await fetchAllFilteredClients();
      if (!rows.length) {
        toast.info("No clients to export");
        return;
      }
      const headers = ["UID", "Client", "Joining Date", "Phone", "Email", "Branch", "Gender", "Packages", "Start Date", "Categories", "Nearest Expiry", "Days Left", "Risk", "Package Names"];
      const data = rows.map((c) => [
        c.uid, c.full_name, c.joining_date || "", c.phone, c.email, c.branch_name, c.gender,
        c.active_package_count, c.earliest_start || "", (c.categories || []).join("; "),
        c.nearest_expiry, c.days_until_expiry ?? "", c.expiry_risk, c.package_names,
      ]);
      const csv = [headers, ...data].map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `active-clients-${moment().format("YYYY-MM-DD")}.csv`;
      link.click();
      toast.success(`Exported ${rows.length} clients`);
    } catch (e) {
      toast.error("Failed to export Excel file");
    }
    setExporting(false);
  };

  const exportPDF = async () => {
    if (!pagination.total_record) {
      toast.info("No clients to export");
      return;
    }
    setExporting(true);
    try {
      const rows = await fetchAllFilteredClients();
      if (!rows.length) {
        toast.info("No clients to export");
        return;
      }
      const doc = new jsPDF({ orientation: "landscape" });
      doc.setFontSize(14);
      doc.text("Active Clients Report", 14, 15);
      doc.setFontSize(9);
      doc.text(`Generated: ${moment().format("DD MMM YYYY HH:mm")}`, 14, 22);
      if (summary) {
        doc.text(`Branch: ${summary.branch_name || selectedBranchLabel} | Clients: ${summary.total_active_clients} | Expiring 30d: ${summary.expiring_within_30_days}`, 14, 28);
      }
      doc.text(`Exported records: ${rows.length}`, 14, summary ? 34 : 28);
      doc.autoTable({
        startY: summary ? 40 : 34,
        head: [["UID", "Client", "Joining", "Phone", "Branch", "Pkgs", "Start", "Expiry", "Days", "Risk"]],
        body: rows.map((c) => [
          c.uid, c.full_name, fmtDate(c.joining_date), c.phone, c.branch_name, c.active_package_count,
          fmtDate(c.earliest_start), fmtDate(c.nearest_expiry), c.days_until_expiry ?? "—", c.expiry_risk,
        ]),
        styles: { fontSize: 7 },
        headStyles: { fillColor: [237, 28, 36] },
      });
      doc.save(`active-clients-${moment().format("YYYY-MM-DD")}.pdf`);
      toast.success(`Exported ${rows.length} clients`);
    } catch (e) {
      toast.error("Failed to export PDF");
    }
    setExporting(false);
  };

  const renderClientRow = (c, detailed = false) => (
    <React.Fragment key={c.client_id}>
      <tr className="acr-row" onClick={() => setExpandedId(expandedId === c.client_id ? null : c.client_id)}>
        <td><i className={`bi bi-chevron-${expandedId === c.client_id ? "down" : "right"}`} /></td>
        <td>{c.uid}</td>
        <td>
          <strong>{c.full_name}</strong>
          {c.frozen_packages > 0 && <span className="badge bg-info ms-1">Frozen</span>}
          {detailed && <div className="small text-muted">{c.gender} · {c.client_type || "Member"}</div>}
        </td>
        <td>{fmtDate(c.joining_date)}</td>
        <td>
          <div className="small">{c.phone}</div>
          <div className="small text-muted">{c.email}</div>
        </td>
        <td>{c.branch_name || "—"}</td>
        <td><span className="badge bg-primary">{c.active_package_count}</span></td>
        {detailed && <td className="small">{c.package_names}</td>}
        <td className="small">{(c.categories || []).join(", ")}</td>
        <td>{fmtDate(c.nearest_expiry)}</td>
        <td>{c.days_until_expiry ?? "—"}</td>
        <td>{riskBadge(c.expiry_risk)}</td>
        <td>
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={(e) => { e.stopPropagation(); openDetail(c.client_id); }}>
            Full detail
          </button>
        </td>
      </tr>
      {expandedId === c.client_id && (
        <tr className="acr-expand-row">
          <td colSpan={detailed ? 13 : 12}>
            <div className="acr-inline-packages">
              <table className="table table-sm mb-0">
                <thead>
                  <tr>
                    <th>Package</th><th>Category</th><th>Sale</th><th>Start</th><th>End</th>
                    <th>Trainer</th><th>Sessions</th><th>Type</th><th>Freeze</th><th>Risk</th>
                  </tr>
                </thead>
                <tbody>
                  {(c.active_packages || []).map((p) => (
                    <tr key={p.order_detail_id}>
                      <td>{p.package_name}</td>
                      <td>{p.category}</td>
                      <td>{fmtDate(p.sale_date)}</td>
                      <td>{fmtDate(p.start_date)}</td>
                      <td>{fmtDate(p.end_date)}</td>
                      <td>{p.trainer_name || "—"}</td>
                      <td>{p.sessions_total != null ? `${p.sessions_remaining}/${p.sessions_total}` : "—"}</td>
                      <td>{p.sale_type || "—"}</td>
                      <td>{p.freeze_status || "—"}</td>
                      <td>{riskBadge(p.expiry_risk)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );

  const renderListTable = (detailed = false) => (
    <div className="table-responsive mt-3">
      <table className="table table-hover acr-table align-middle">
        <thead>
          <tr>
            <th></th><th>UID</th><th>Client</th><th>Joining date</th><th>Contact</th><th>Branch</th><th>Pkgs</th>
            {detailed && <th>Package names</th>}
            <th>Categories</th><th>Nearest expiry</th><th>Days</th><th>Risk</th><th></th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={detailed ? 13 : 12} className="text-center py-4"><Spinner animation="border" size="sm" /></td></tr>
          ) : displayClients.length === 0 ? (
            <tr><td colSpan={detailed ? 13 : 12} className="text-center text-muted py-4">No active clients match your filters.</td></tr>
          ) : displayClients.map((c) => renderClientRow(c, detailed))}
        </tbody>
      </table>
    </div>
  );

  const renderManagementView = () => (
    <div className="acr-management-view mt-3">
      <div className="row g-3">
        <div className="col-lg-6">
          <div className="acr-mgmt-card">
            <h6>Clients by branch</h6>
            <table className="table table-sm mb-0">
              <thead><tr><th>Branch</th><th>Clients</th><th>Packages</th></tr></thead>
              <tbody>
                {(management?.by_branch || []).map((row, i) => (
                  <tr key={i}>
                    <td>{row.branch_name || "—"}</td>
                    <td>{row.client_count}</td>
                    <td>{row.package_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="col-lg-6">
          <div className="acr-mgmt-card">
            <h6>Clients by category</h6>
            <table className="table table-sm mb-0">
              <thead><tr><th>Category</th><th>Clients</th><th>Packages</th></tr></thead>
              <tbody>
                {(management?.by_category || []).map((row) => (
                  <tr key={row.category_id}>
                    <td>{row.category}</td>
                    <td>{row.client_count}</td>
                    <td>{row.package_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="acr-mgmt-card mt-3">
        <h6>Expiry risk overview</h6>
        <div className="acr-summary-row">
          {(management?.by_risk || []).map((r) => (
            <button
              key={r.risk}
              type="button"
              className={`acr-summary-card ${applied.riskLevel === r.risk ? "active" : ""}`}
              onClick={() => { setRiskLevel(riskLevel === r.risk ? "" : r.risk); setViewMode("list"); }}
            >
              <div className={`acr-summary-num ${r.risk === "critical" ? "text-danger" : ""}`}>{r.client_count}</div>
              <div className="acr-summary-label">{r.label}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const renderByCategoryView = () => (
    <div className="acr-category-view mt-3">
      {clientsByCategory.map(([cat, list]) => {
        const collapsed = collapsedCategories.has(cat);
        return (
          <div key={cat} className="acr-category-block">
            <button
              type="button"
              className="acr-category-header"
              onClick={() => {
                const next = new Set(collapsedCategories);
                if (collapsed) next.delete(cat); else next.add(cat);
                setCollapsedCategories(next);
              }}
            >
              <i className={`bi bi-chevron-${collapsed ? "right" : "down"} me-2`} />
              <strong>{cat}</strong>
              <span className="badge bg-secondary ms-2">{list.length} clients</span>
            </button>
            {!collapsed && (
              <div className="acr-category-body">
                <table className="table table-sm table-hover mb-0">
                  <thead><tr><th>Client</th><th>Joining date</th><th>UID</th><th>Branch</th><th>Expiry</th><th>Days</th><th></th></tr></thead>
                  <tbody>
                    {list.map((c) => (
                      <tr key={`${cat}-${c.client_id}`}>
                        <td>{c.full_name}</td>
                        <td>{fmtDate(c.joining_date)}</td>
                        <td>{c.uid}</td>
                        <td>{c.branch_name}</td>
                        <td>{fmtDate(c.nearest_expiry)}</td>
                        <td>{riskBadge(c.expiry_risk)}</td>
                        <td>
                          <button type="button" className="btn btn-sm btn-link p-0" onClick={() => openDetail(c.client_id)}>Detail</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const renderDetailContent = () => {
    if (detailLoading) return <div className="text-center py-5"><Spinner animation="border" /></div>;
    if (!detail) return <p className="text-muted text-center py-4">Could not load client details.</p>;
    const p = detail.profile;
    switch (detailTab) {
      case "overview":
        return (
          <div className="acr-detail-grid">
            <div className="acr-detail-card"><h6>Contact</h6><dl>
              <dt>UID</dt><dd>{p.uid}</dd><dt>Joining date</dt><dd>{fmtDate(p.joining_date || p.registration_date)}</dd><dt>Phone</dt><dd>{p.phone || "—"}</dd>
              <dt>Email</dt><dd>{p.email || "—"}</dd><dt>Gender</dt><dd>{p.gender || "—"}</dd>
              <dt>Branch</dt><dd>{p.branch_name || "—"}</dd>
            </dl></div>
            <div className="acr-detail-card"><h6>Registration</h6><dl>
              <dt>Package</dt><dd>{p.registration_package || "—"}</dd>
              <dt>Date</dt><dd>{fmtDate(p.registration_date)}</dd>
            </dl></div>
            <div className="acr-detail-card"><h6>Counts</h6><dl>
              <dt>Active packages</dt><dd>{detail.counts?.active_packages ?? 0}</dd>
              <dt>Gym check-ins</dt><dd>{detail.counts?.gym_checkins ?? 0}</dd>
              <dt>Payments</dt><dd>{detail.counts?.payments ?? 0}</dd>
            </dl></div>
          </div>
        );
      case "packages":
        return (
          <div className="table-responsive">
            <table className="table table-sm acr-table">
              <thead><tr><th>Package</th><th>Category</th><th>Start</th><th>End</th><th>Trainer</th><th>Sessions</th><th>Risk</th></tr></thead>
              <tbody>
                {(detail.active_packages || []).map((pkg) => (
                  <tr key={pkg.order_detail_id}>
                    <td>{pkg.package_name}</td><td>{pkg.category}</td>
                    <td>{fmtDate(pkg.start_date)}</td><td>{fmtDate(pkg.end_date)}</td>
                    <td>{pkg.trainer_name || "—"}</td>
                    <td>{pkg.sessions_total != null ? `${pkg.sessions_delivered}/${pkg.sessions_total}` : "—"}</td>
                    <td>{riskBadge(pkg.expiry_risk)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      default:
        return renderDetailTabLegacy(detailTab, detail);
    }
  };

  const renderDetailTabLegacy = (tab, detail) => {
    const tables = {
      history: { cols: ["Package", "Category", "Status", "Sale", "End"], rows: (detail.package_history || []).map((pkg) => [pkg.package_name, pkg.category, pkg.is_active ? "Active" : "Inactive", fmtDate(pkg.sale_date), fmtDate(pkg.end_date)]) },
      gym: { cols: ["Date", "Check in", "Check out"], rows: (detail.gym_attendance || []).map((a) => [fmtDate(a.date), a.checkin_time_12h || "—", a.checkout_time_12h || "—"]) },
      sessions: { cols: ["Date", "Package", "Trainer", "Valid"], rows: (detail.session_attendance || []).map((s) => [fmtDate(s.date), s.package_name, s.trainer_name || "—", s.validate_status === "1" ? "Yes" : "No"]) },
      payments: { cols: ["Date", "Order", "Amount", "Method"], rows: (detail.payments || []).map((pay) => [fmtDate(pay.date), pay.order_id, pay.received, pay.payment_method || "—"]) },
      freezing: { cols: ["Start", "End", "Reason"], rows: (detail.freezing || []).map((f) => [fmtDate(f.start_date), fmtDate(f.end_date), f.reason || "—"]) },
      cards: { cols: ["Card #", "Status"], rows: (detail.cards || []).map((c) => [c.number, c.status]) },
    };
    const t = tables[tab];
    if (!t) return null;
    return (
      <div className="table-responsive">
        <table className="table table-sm acr-table">
          <thead><tr>{t.cols.map((c) => <th key={c}>{c}</th>)}</tr></thead>
          <tbody>{t.rows.map((row, i) => <tr key={i}>{row.map((cell, j) => <td key={j}>{cell}</td>)}</tr>)}</tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="wrapper active-clients-report">
      <main className="page-content customer-contnent">
        <div className="site-main-design">
        <div className="card acr-card">
          <div className="card-body">
            <div className="acr-header">
              <div>
                <MenuButton />
                <h4 className="d-inline-block ms-2 mb-0">Active Clients Report</h4>
                <p className="text-muted small mb-0 mt-1">
                  Service packages only — excludes registration, cafe &amp; freezing add-ons.
                  <span className="acr-branch-pill ms-2"><i className="bi bi-building me-1" />{selectedBranchLabel}</span>
                </p>
              </div>
              <div className="acr-export-btns">
                <button type="button" className="btn btn-outline-danger btn-sm" onClick={exportPDF} disabled={exporting || !pagination.total_record}>
                  {exporting ? <Spinner animation="border" size="sm" className="me-1" /> : <i className="bi bi-file-pdf me-1" />}PDF
                </button>
                <button type="button" className="btn btn-outline-success btn-sm" onClick={exportCSV} disabled={exporting || !pagination.total_record}>
                  {exporting ? <Spinner animation="border" size="sm" className="me-1" /> : <i className="bi bi-file-earmark-spreadsheet me-1" />}Excel
                </button>
              </div>
            </div>

            {summary && (
              <div className="acr-summary-row">
                <button type="button" className={`acr-summary-card ${applied.expiryWindow === "all" && !applied.riskLevel ? "active" : ""}`} onClick={() => { setExpiryWindow("all"); setRiskLevel(""); }}>
                  <div className="acr-summary-num">{summary.total_active_clients}</div><div className="acr-summary-label">Active clients (membership + gym)</div>
                </button>
                <button type="button" className={`acr-summary-card ${applied.expiryWindow === "30" ? "active" : ""}`} onClick={() => { setExpiryWindow("30"); setRiskLevel(""); }}>
                  <div className="acr-summary-num text-danger">{summary.expiring_within_30_days}</div><div className="acr-summary-label">≤ 1 month</div>
                </button>
                <button type="button" className={`acr-summary-card ${applied.expiryWindow === "60" ? "active" : ""}`} onClick={() => { setExpiryWindow("60"); setRiskLevel(""); }}>
                  <div className="acr-summary-num text-warning">{summary.expiring_within_60_days}</div><div className="acr-summary-label">≤ 2 months</div>
                </button>
                <button type="button" className={`acr-summary-card ${applied.expiryWindow === "90" ? "active" : ""}`} onClick={() => { setExpiryWindow("90"); setRiskLevel(""); }}>
                  <div className="acr-summary-num">{summary.expiring_within_90_days}</div><div className="acr-summary-label">≤ 3 months</div>
                </button>
                <div className="acr-summary-card static"><div className="acr-summary-num">{summary.total_active_packages}</div><div className="acr-summary-label">Package lines</div></div>
                <div className="acr-summary-card static"><div className="acr-summary-num">{summary.frozen_package_lines}</div><div className="acr-summary-label">Frozen</div></div>
              </div>
            )}

            <div className="acr-view-tabs">
              {VIEW_MODES.map((v) => (
                <button key={v.key} type="button" className={`acr-view-tab ${viewMode === v.key ? "active" : ""}`} onClick={() => setViewMode(v.key)}>
                  <i className={`bi ${v.icon} me-1`} />{v.label}
                </button>
              ))}
            </div>

            <div className="acr-filters">
              <div className="row g-2 acr-filter-row">
                <div className="col-md-2">
                  <label className="acr-filter-label">Branch <span className="text-danger">*</span></label>
                  <select
                    className="form-select form-select-sm acr-branch-select"
                    value={effectiveBranchId === "all" ? "all" : String(effectiveBranchId)}
                    onChange={(e) => handleBranchChange(e.target.value)}
                    disabled={!isCrossBranchAdmin(role)}
                  >
                    {canUseAllBranches(role) ? <option value="all">All branches</option> : null}
                    {branchChoices.map((b) => (
                      <option key={b.id} value={String(b.id)}>{b.name}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="acr-filter-label">Client</label>
                  <div className="position-relative acr-client-input-wrap" ref={clientDropdownRef}>
                    <div className="input-group input-group-sm acr-filter-control">
                      <span className="input-group-text bg-white"><i className="bi bi-search" /></span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Type name, phone or UID..."
                        value={clientSearchTerm}
                        onChange={handleClientSearch}
                        onFocus={() => {
                          setShowClientDropdown(true);
                          setFilteredClients(filterClientsForPicker(clientSearchTerm, allClients));
                        }}
                        onKeyDown={(e) => { if (e.key === "Enter") handleApplySearch(); }}
                        autoComplete="off"
                      />
                      {clientSearchTerm ? (
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={clearClientSearch}>Clear</button>
                      ) : null}
                    </div>
                    {showClientDropdown && filteredClients.length > 0 && (
                      <div className="acr-client-dropdown">
                        <div className="acr-client-dropdown-meta">{filteredClients.length} of {allClients.length} clients</div>
                        {filteredClients.map((c) => (
                          <button
                            key={c.id}
                            type="button"
                            className={`acr-client-dropdown-item${String(c.id) === String(draftClientId) ? " active" : ""}`}
                            onClick={() => handleClientSelect(c)}
                          >
                            <span className="acr-client-dropdown-name">{c.name}</span>
                            <span className="acr-client-dropdown-sub">{[c.uid ? `ID: ${c.uid}` : null, c.phone].filter(Boolean).join(" · ")}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {showClientDropdown && clientSearchTerm && filteredClients.length === 0 && (
                      <div className="acr-client-dropdown acr-client-dropdown-empty">No clients match</div>
                    )}
                  </div>
                </div>
                <div className="col-md-2">
                  <label className="acr-filter-label">Gender</label>
                  <select className="form-select form-select-sm" value={gender} onChange={(e) => setGender(e.target.value)}>
                    <option value="">All</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="acr-filter-label">Category</label>
                  <select className="form-select form-select-sm" value={category} onChange={(e) => { setCategory(e.target.value); setPackageId(""); }}>
                    {CATEGORY_OPTIONS.map((c) => <option key={c.value || "all"} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="acr-filter-label">Package</label>
                  <select className="form-select form-select-sm" value={packageId} onChange={(e) => setPackageId(e.target.value)}>
                    <option value="">All packages</option>
                    {packageOptions.map((p) => (
                      <option key={p.id} value={String(p.id)}>{p.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="acr-quick-dates mt-2">
                <span className="acr-filter-label me-2">Quick expiry:</span>
                {QUICK_DATES.map((q) => (
                  <button key={q.key} type="button" className="btn btn-sm btn-outline-secondary me-1 mb-1" onClick={() => applyQuickDate(q.key)}>{q.label}</button>
                ))}
              </div>

              <div className="row g-2 mt-1 acr-filter-row">
                <div className="col-md-2">
                  <label className="acr-filter-label">Start from</label>
                  <input type="date" className="form-control form-control-sm acr-filter-control" value={startFrom} onChange={(e) => setStartFrom(e.target.value)} />
                </div>
                <div className="col-md-2">
                  <label className="acr-filter-label">Start to</label>
                  <input type="date" className="form-control form-control-sm acr-filter-control" value={startTo} onChange={(e) => setStartTo(e.target.value)} />
                </div>
                <div className="col-md-2">
                  <label className="acr-filter-label">Expiry from</label>
                  <input type="date" className="form-control form-control-sm acr-filter-control" value={expiryFrom} onChange={(e) => { setExpiryFrom(e.target.value); setExpiryWindow("all"); }} />
                </div>
                <div className="col-md-2">
                  <label className="acr-filter-label">Expiry to</label>
                  <input type="date" className="form-control form-control-sm acr-filter-control" value={expiryTo} onChange={(e) => { setExpiryTo(e.target.value); setExpiryWindow("all"); }} />
                </div>
                <div className="col-md-2">
                  <label className="acr-filter-label">Expiry window</label>
                  <select className="form-select form-select-sm" value={expiryWindow} onChange={(e) => setExpiryWindow(e.target.value)}>
                    {EXPIRY_FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="acr-filter-label">Per page</label>
                  <select className="form-select form-select-sm" value={limit} onChange={(e) => handlePerPageChange(e.target.value)}>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                  </select>
                </div>
              </div>

              <div className="row g-2 mt-1 align-items-end">
                <div className="col-auto">
                  <button type="button" className="btn btn-sm btn-link" onClick={() => setShowMoreFilters(!showMoreFilters)}>
                    {showMoreFilters ? "Less filters" : "More filters"}
                  </button>
                </div>
                <div className="col-auto">
                  <button type="button" className="btn btn-sm btn-danger" onClick={handleApplySearch}>
                    <i className="bi bi-search me-1" />Search
                  </button>
                </div>
                <div className="col-auto">
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={resetFilters}>Reset</button>
                </div>
                {pagination.total_record > 0 && (
                  <div className="col-auto ms-md-auto">
                    <span className="text-muted small">{pagination.total_record} clients match filters · PDF/Excel exports all filtered rows</span>
                  </div>
                )}
              </div>

              {showMoreFilters && (
                <div className="row g-2 mt-1">
                  <div className="col-md-2">
                    <label className="acr-filter-label">Client type</label>
                    <select className="form-select form-select-sm" value={clientType} onChange={(e) => setClientType(e.target.value)}>
                      <option value="">All</option><option value="Member">Member</option><option value="Guest">Guest</option>
                    </select>
                  </div>
                  <div className="col-md-2">
                    <label className="acr-filter-label">Sale type</label>
                    <select className="form-select form-select-sm" value={saleType} onChange={(e) => setSaleType(e.target.value)}>
                      <option value="">All</option><option value="New">New</option><option value="Renew">Renew</option>
                    </select>
                  </div>
                  <div className="col-md-2">
                    <label className="acr-filter-label">Risk level</label>
                    <select className="form-select form-select-sm" value={riskLevel} onChange={(e) => setRiskLevel(e.target.value)}>
                      <option value="">All</option><option value="critical">≤ 1 month</option><option value="warning">≤ 2 months</option><option value="ok">Healthy</option><option value="expired">Expired</option>
                    </select>
                  </div>
                  <div className="col-md-2">
                    <label className="acr-filter-label">Freeze</label>
                    <select className="form-select form-select-sm" value={freezeStatus} onChange={(e) => setFreezeStatus(e.target.value)}>
                      <option value="">All</option><option value="Active">Active</option><option value="Frozen">Frozen</option>
                    </select>
                  </div>
                  <div className="col-md-2">
                    <label className="acr-filter-label">Trainer</label>
                    <input type="text" className="form-control form-control-sm" placeholder="Trainer name" value={trainerName} onChange={(e) => setTrainerName(e.target.value)} />
                  </div>
                  <div className="col-md-1">
                    <label className="acr-filter-label">Sort</label>
                    <select className="form-select form-select-sm" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                      <option value="nearest_expiry">Expiry</option><option value="name">Name</option><option value="packages">Packages</option><option value="branch">Branch</option>
                    </select>
                  </div>
                  <div className="col-md-1">
                    <label className="acr-filter-label">Dir</label>
                    <select className="form-select form-select-sm" value={sortDir} onChange={(e) => setSortDir(e.target.value)}>
                      <option value="asc">Asc</option><option value="desc">Desc</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {viewMode === "management" && renderManagementView()}
            {viewMode === "by_category" && renderByCategoryView()}
            {(viewMode === "list" || viewMode === "detailed" || viewMode === "expiring") && renderListTable(viewMode === "detailed")}

            {pagination.total_record > applied.limit && viewMode !== "management" && (
              <div className="d-flex justify-content-center mt-3">
                <Pagination activePage={page} itemsCountPerPage={applied.limit} totalItemsCount={pagination.total_record} pageRangeDisplayed={5} onChange={(p) => loadReport(p)} itemClass="page-item" linkClass="page-link" />
              </div>
            )}
          </div>
        </div>
        </div>
      </main>

      <Modal show={detailOpen} onHide={() => setDetailOpen(false)} size="xl" scrollable className="acr-detail-modal">
        <Modal.Header closeButton>
          <Modal.Title>{detail?.profile?.full_name || "Client detail"} {detail?.profile?.uid && <span className="text-muted small ms-2">#{detail.profile.uid}</span>}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="acr-detail-tabs">
            {DETAIL_TABS.map((t) => (
              <button key={t.key} type="button" className={`acr-detail-tab ${detailTab === t.key ? "active" : ""}`} onClick={() => setDetailTab(t.key)}>
                <i className={`bi ${t.icon} me-1`} />{t.label}
              </button>
            ))}
          </div>
          {renderDetailContent()}
        </Modal.Body>
      </Modal>

      <div className={`loader ${loading ? "in" : ""}`}><div className="spinner-border main-spin" /></div>
    </div>
  );
}
